
import { Vue, Component } from 'vue-property-decorator';
import { BModal } from 'bootstrap-vue';
import StateButton from '@/components/inputs/StateButton.vue';
import programx from '@/store/modules/programx';
import mwx from '@/store/modules/mediumwarex';
import progressToastx from '@/store/modules/progressToastx';
import { ProgramData, AccountMinimumData, EditState } from '@/store/models.def';

interface AccountSelection {
	account: AccountMinimumData;
	selectedProgramId: string;
	originalProgramId: string;
	hasChanged: boolean;
	applying: boolean;
}

@Component({
	components: {
		StateButton,
	},
})
export default class DeleteProgramModal extends Vue {
	public program: ProgramData | null = null;
	public accountSelections: AccountSelection[] = [];
	public loading: boolean = false;
	public deleteState: EditState = 'active';
	public pendingChange: AccountSelection | null = null;

	public get canDelete(): boolean {
		return !this.loading && this.accountSelections.length === 0;
	}

	public get deleteButtonLabel(): string {
		const labels: Record<EditState, string> = {
			active: 'Delete Program',
			sending: 'Deleting...',
			done: 'Deleted!',
		};
		return labels[this.deleteState];
	}

	public get programOptions() {
		const currentProgramId = this.program ? this.program.id : '';
		return programx.allPrograms
			.filter((p) => p.id !== currentProgramId)
			.map((p) => ({
				value: p.id,
				text: `${p.id} (${p.softwareType})`,
			}));
	}

	public show(program: ProgramData): void {
		this.program = program;
		const modal = this.$refs.deleteProgramModal as BModal;
		if (modal) {
			modal.show();
		}
	}

	public hide(): void {
		const modal = this.$refs.deleteProgramModal as BModal;
		if (modal) {
			modal.hide();
		}
	}

	public resetModal(): void {
		this.program = null;
		this.accountSelections = [];
		this.loading = false;
		this.deleteState = 'active';
		this.pendingChange = null;
	}

	public async loadAccounts(): Promise<void> {
		if (!this.program) { return; }

		this.loading = true;
		try {
			let result = await programx.getAccountsUsingProgram(this.program.id);
			// Handle case where result comes back as array
			if (Array.isArray(result) && result.length > 0 && Array.isArray(result[0])) {
				result = result[0];
			}
			const accounts: AccountMinimumData[] = result || [];

			// Initialize account selections
			this.accountSelections = accounts.map((account) => ({
				account,
				selectedProgramId: this.program!.id,
				originalProgramId: this.program!.id,
				hasChanged: false,
				applying: false,
			}));
		} catch (error) {
			console.error('Error loading accounts:', error);
			this.accountSelections = [];
		} finally {
			this.loading = false;
		}
	}

	public onProgramChange(item: AccountSelection): void {
		item.hasChanged = item.selectedProgramId !== item.originalProgramId;
	}

	public showApplyWarning(item: AccountSelection): void {
		this.pendingChange = item;
		const modal = this.$refs.applyWarningModal as BModal;
		if (modal) {
			modal.show();
		}
	}

	public async applyProgramChange(): Promise<void> {
		if (!this.pendingChange) { return; }

		const item = this.pendingChange;
		item.applying = true;

		const toastId = await progressToastx.add({
			title: 'Change Program',
			state: `Changing program for ${item.account.companyName}...`,
			variant: 'info',
		});

		try {
			let result: any = await mwx.changeProgramId({
				accountId: item.account.accountId,
				programId: item.selectedProgramId,
			});
			// Handle case where result comes back as array
			if (Array.isArray(result)) { result = result[0]; }

			if (result && result.success) {
				progressToastx.done({
					id: toastId,
					state: `Program changed successfully!`,
				});

				// Remove this account from the list since it's no longer using this program
				this.accountSelections = this.accountSelections.filter(
					(a) => a.account.accountId !== item.account.accountId,
				);
			} else {
				progressToastx.error({
					id: toastId,
					state: result && result.errorMessage || 'Failed to change program',
				});
				item.applying = false;
			}
		} catch (error: any) {
			progressToastx.error({
				id: toastId,
				state: error.message || 'An error occurred',
			});
			item.applying = false;
		}

		this.pendingChange = null;
	}

	public async confirmDelete(): Promise<void> {
		if (!this.program || !this.canDelete) { return; }

		const toastId = await progressToastx.add({
			title: 'Delete Program',
			state: `Deleting ${this.program.id}...`,
			variant: 'info',
		});

		this.deleteState = 'sending';

		try {
			let result: any = await programx.deleteProgram(this.program.id);
			// Handle case where result comes back as array
			if (Array.isArray(result)) { result = result[0]; }

			if (result && result.success) {
				progressToastx.done({
					id: toastId,
					state: `Program ${this.program.id} deleted successfully!`,
				});
				this.$emit('deleted', this.program.id);
				this.hide();
			} else {
				progressToastx.error({
					id: toastId,
					state: result && result.errorMessage || 'Failed to delete program',
				});
			}
		} catch (error: any) {
			progressToastx.error({
				id: toastId,
				state: error.message || 'An error occurred',
			});
		} finally {
			this.deleteState = 'active';
		}
	}
}
